import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from './cn';
import { FOCUS_RING } from './Button';

/**
 * Multi-seleção em dropdown com checkboxes — filtros de lista.
 *
 * É um dropdown, não um modal: fecha ao clicar fora. Com muitas opções
 * (ex.: POPs do ERP) ganha um campo de busca no topo.
 */
export interface MultiSelectOption {
  value: string;
  label: string;
}

const BUSCA_A_PARTIR_DE = 9;

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  plural,
  className,
}: {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (sel: string[]) => void;
  /** Texto com nada selecionado — ex: "Todos" / "Todas" */
  placeholder: string;
  /** Sufixo do contador — ex: "status", "cidades" */
  plural: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setBusca('');
      return;
    }
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return termo ? options.filter((o) => o.label.toLowerCase().includes(termo)) : options;
  }, [options, busca]);

  function toggle(value: string): void {
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);
  }

  const labelOf = (value: string): string => options.find((o) => o.value === value)?.label ?? value;

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-7 min-w-[10rem] items-center justify-between gap-2 rounded-[7px] border px-[11px] text-[12.5px] shadow-xs transition-colors',
          FOCUS_RING,
          selected.length > 0
            ? 'border-primary-line bg-primary-soft text-primary'
            : 'border-line bg-surface text-fg hover:bg-hover',
        )}
      >
        <span className="max-w-[12rem] truncate">
          {selected.length === 0
            ? placeholder
            : selected.length === 1
              ? labelOf(selected[0]!)
              : `${selected.length} ${plural}`}
        </span>
        <ChevronDown size={13} className="flex-none" />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-64 overflow-hidden rounded-lg border border-line bg-surface-2 shadow-md animate-atmos-in">
          {options.length >= BUSCA_A_PARTIR_DE && (
            <div className="relative border-b border-line-subtle p-1.5">
              <Search
                size={13}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
              />
              <input
                autoFocus
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Filtrar…"
                className="h-7 w-full rounded-md bg-sunken pl-7 pr-2 text-[12.5px] text-fg outline-none placeholder:text-muted"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto py-1">
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full border-b border-line-subtle px-3 py-1.5 text-left text-xs text-fg-2 hover:bg-hover hover:text-fg"
              >
                Limpar seleção ({selected.length})
              </button>
            )}
            {visiveis.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs text-muted">Nada encontrado.</p>
            ) : (
              visiveis.map((o) => (
                <label
                  key={o.value}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] text-fg hover:bg-hover"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(o.value)}
                    onChange={() => toggle(o.value)}
                    className="h-3.5 w-3.5 flex-none accent-[var(--primary)]"
                  />
                  <span className="truncate" title={o.label}>
                    {o.label}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
